import React, { useState, useEffect } from 'react';
import { formatNumber } from '../utils';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function StockManagementPage({ customers, onEdit, onDelete, onItemAction, refreshTrigger }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch items when customer filter changes (only if a customer is selected)
  useEffect(() => {
    // Don't fetch if no customer selected
    if (!clientFilter) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    
    let url = `${API_URL}/items`;
    if (clientFilter !== 'ALL') {
      url += `?clientId=${clientFilter}`;
    }
    
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch items');
        return res.json();
      })
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [clientFilter, refreshTrigger]);
  
  // Enhanced filtering with search functionality
  const filteredItems = items.filter(item => {
    // Client filter
    const clientMatch = clientFilter === 'ALL' || item.clientId === clientFilter;
    
    // Search filter - search across multiple fields
    const searchMatch = !searchTerm || [
      item.productCode,
      item.barcode,
      item.mainCategory,
      item.variantDesc,
      item.client?.name,
      item.labelSize
    ].some(field => 
      field && field.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return clientMatch && searchMatch;
  });

  // Handle keyboard shortcuts
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredItems.length > 0) {
      // Open Stock Actions modal for first result
      onItemAction(filteredItems[0]);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{width:'100%',maxWidth:1200,margin:'0 auto',marginTop:24,background:'#fff',borderRadius:24,boxShadow:'0 2px 12px #1976d222',padding:32,textAlign:'center'}}>
        <div style={{padding:40,fontSize:18,color:'#1976d2'}}>
          <div style={{marginBottom:16}}>Loading items...</div>
          <div style={{fontSize:14,color:'#666'}}>Please wait</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{width:'100%',maxWidth:1200,margin:'0 auto',marginTop:24,background:'#fff',borderRadius:24,boxShadow:'0 2px 12px #1976d222',padding:32}}>
        <div style={{padding:40,color:'#d32f2f',textAlign:'center'}}>
          <div style={{fontSize:18,fontWeight:600,marginBottom:8}}>Error loading items</div>
          <div style={{fontSize:14}}>{error}</div>
          <button 
            onClick={() => setClientFilter('ALL')}
            style={{marginTop:16,padding:'8px 16px',background:'#1976d2',color:'#fff',border:'none',borderRadius:8,cursor:'pointer'}}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{width:'100%',maxWidth:1200,margin:'0 auto',marginTop:24,background:'#fff',borderRadius:24,boxShadow:'0 2px 12px #1976d222',padding:32}}>
      {/* Prominent Search Bar */}
      <div style={{marginBottom:24}}>
        <div style={{position:'relative',maxWidth:600}}>
          <input
            type="text"
            placeholder="🔍 Search items (Product Code, Barcode, Description, Customer...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            style={{
              width:'100%',
              fontSize:16,
              padding:'12px 20px',
              borderRadius:12,
              border:'2px solid #1976d2',
              outline:'none',
              boxShadow:'0 2px 8px #1976d211',
              background:'#f8fafc',
              fontWeight:500,
              color:'#1976d2',
              transition:'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.background = '#fff';
              e.target.style.boxShadow = '0 4px 12px #1976d222';
            }}
            onBlur={(e) => {
              e.target.style.background = '#f8fafc';
              e.target.style.boxShadow = '0 2px 8px #1976d211';
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position:'absolute',
                right:12,
                top:'50%',
                transform:'translateY(-50%)',
                background:'none',
                border:'none',
                fontSize:18,
                color:'#1976d2',
                cursor:'pointer',
                padding:4
              }}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div style={{marginTop:8,fontSize:14,color:'#666'}}>
            Found {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
            {filteredItems.length > 0 && ' (Press Enter to open first result)'}
          </div>
        )}
      </div>
      
      <div style={{display:'flex', alignItems:'center', marginBottom:14}}>
        <h3 style={{marginTop:0,marginBottom:0,color:'#1976d2',fontWeight:700,fontSize:'1.25rem',letterSpacing:1,marginRight:18}}>Stock Management</h3>
        <select
          value={clientFilter}
          onChange={e => setClientFilter(e.target.value)}
          style={{
            fontSize:15,
            borderRadius:8,
            border:'1px solid #1976d2',
            padding:'6px 16px',
            fontWeight:600,
            color:'#1976d2',
            background:'#f8fafc',
            marginLeft:4,
            outline:'none',
            boxShadow:'0 1px 6px #1976d211',
            cursor:'pointer'
          }}
        >
          <option value="">Select Customer</option>
          <option value="ALL">All Customers</option>
          {customers.map(cust => (
            <option key={cust.id} value={cust.id}>{cust.name}</option>
          ))}
        </select>
      </div>
      {!clientFilter ? (
        <div style={{textAlign:'center',padding:60,color:'#666'}}>
          <div style={{fontSize:20,fontWeight:600,marginBottom:12,color:'#1976d2'}}>
            👆 Please select a customer to view stock items
          </div>
          <div style={{fontSize:15}}>
            Choose "All Customers" to see all items, or select a specific customer
          </div>
        </div>
      ) : (
        <>
          <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',marginBottom:10}}>
            <span style={{fontWeight:700,fontSize:18,color:'#1976d2',background:'#e3f2fd',padding:'8px 22px',borderRadius:10,boxShadow:'0 1px 4px #1976d211'}}>Grand Total Value: {(() => {
              const total = filteredItems.reduce((sum, i) => sum + ((i.currentQty * i.cost) / 1000 || 0), 0);
              return `R${formatNumber(total)}`;
            })()}</span>
          </div>
          <table style={{width:'100%',marginTop:16}}>
        <thead>
          <tr><th>Customer</th><th>Product Code</th><th>Description</th><th>Variant</th><th>Label Size</th><th>Min Qty</th><th>Current Qty</th><th>Available</th><th>Cost (R/1000)</th><th>Total Value</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filteredItems.map((i, index) => {
            // Highlight first result if searching
            const isFirstResult = searchTerm && index === 0;
            const baseStyle = {
              cursor:'pointer',
              transition:'background 0.13s',
              ...(isFirstResult && {
                background:'#e8f5e8',
                border:'2px solid #4caf50'
              })
            };
            
            return (
              <tr key={i.id}
                style={baseStyle}
                onClick={e => {
                  // Only trigger if not clicking on a button
                  if (e.target.tagName !== 'BUTTON') onItemAction(i);
                }}
                onMouseOver={e => {
                  if (!isFirstResult) {
                    e.currentTarget.style.background = '#e3f2fd';
                  }
                }}
                onMouseOut={e => {
                  if (!isFirstResult) {
                    e.currentTarget.style.background = '';
                  }
                }}
              >
              <td>{i.client ? i.client.name : ''}</td>
              <td>{i.productCode}</td>
              <td>{i.mainCategory}</td>
              <td>{i.variantDesc}</td>
              <td>{i.labelSize || '-'}</td>
              <td>{formatNumber(i.minQty)}</td>
              <td>{formatNumber(i.currentQty)}</td>
              <td style={{color: '#2e7d32', fontWeight: 'bold'}}>{formatNumber(i.currentQty - (i.bookedQty || 0))}</td>
              <td>{i.cost ? `R${formatNumber(i.cost)}` : '-'}</td>
              <td>{i.cost ? `R${formatNumber((i.currentQty * i.cost) / 1000)}` : '-'}</td>
              <td>
                <button onClick={e => { e.stopPropagation(); onEdit(i); }}>Edit</button>
                <button
                  onClick={e => { e.stopPropagation(); if (i.currentQty === 0) onDelete(i); }}
                  style={{marginLeft:8, opacity: i.currentQty === 0 ? 1 : 0.5, cursor: i.currentQty === 0 ? 'pointer' : 'not-allowed'}}
                  disabled={i.currentQty !== 0}
                  title={i.currentQty !== 0 ? 'Cannot delete: Current Qty must be 0' : 'Delete this item'}
                >
                  Delete
                </button>
              </td>
            </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="8" style={{textAlign:'right',fontWeight:600}}>Grand Total Value:</td>
            <td colSpan="2" style={{fontWeight:700,color:'#1976d2'}}>
              {(() => {
                const total = filteredItems.reduce((sum, i) => sum + ((i.currentQty * i.cost) / 1000 || 0), 0);
                return `R${formatNumber(total)}`;
              })()}
            </td>
          </tr>
        </tfoot>
      </table>
        </>
      )}
    </div>
  );
}
